import axios, {
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { API_BASE_URL } from "./urlEndpoints";
import { toast } from "../utils/toast";
import * as _ from "lodash";
import { emitter } from "../utils/emitter";
import { NOT_AUTHORIZED_EVENT } from "../constants/commons";

export type Method =
  | "get"
  | "GET"
  | "delete"
  | "DELETE"
  | "head"
  | "HEAD"
  | "options"
  | "OPTIONS"
  | "post"
  | "POST"
  | "put"
  | "PUT"
  | "patch"
  | "PATCH";

class API {
  static namespace =
    new URL(window.location.href).searchParams.get("namespace") ||
    "PARTNER_FIT";

  static get = (
    route: string,
    headers?: object,
    baseURL = API_BASE_URL
  ): AxiosPromise => {
    const allHeaders = API.getAllHeaders(headers);
    return API.xhr(route, allHeaders, {}, "get", baseURL);
  };
  static post = (
    route: string,
    params: any,
    headers?: object,
    baseURL = API_BASE_URL
  ): AxiosPromise => {
    // Checking to see whether the POST call has a custom_header Obj which I pass in the registration pages

    if (params && params.customHeader) {
      headers = params.customHeader;
    }
    let paramsObj;
    paramsObj = _.omit(params, ["customHeader"]);
    const allHeaders = API.getAllHeaders(headers);
    return API.xhr(route, allHeaders, paramsObj, "post", baseURL);
  };

  static put = (
    route: string,
    params: object,
    headers?: object,
    onUploadProgress = (progressEvent: any) => {},
    stringify?: boolean,
    baseURL = API_BASE_URL
  ): AxiosPromise => {
    const allHeaders = API.getAllHeaders(headers);
    return API.xhr(
      route,
      allHeaders,
      params,
      "put",
      baseURL,
      stringify,
      onUploadProgress
    );
  };

  static patch = (
    route: string,
    params: object,
    headers?: object,
    stringify?: boolean,
    baseURL = API_BASE_URL
  ): AxiosPromise => {
    const allHeaders = API.getAllHeaders(headers);
    return API.xhr(route, allHeaders, params, "patch", baseURL, stringify);
  };

  static delete = (
    route: string,
    params: object,
    headers?: object,
    baseURL = API_BASE_URL
  ): AxiosPromise => {
    const allHeaders = API.getAllHeaders(headers);
    return API.xhr(route, allHeaders, params, "delete", baseURL);
  };
  static xhr = (
    route: string,
    headers: object,
    params: object,
    method: Method,
    baseURL = API_BASE_URL,
    stringify: boolean = false,
    onUploadProgress = (progressEvent: any) => {}
  ): any => {
    // file object can't be stringified hence using a flag to determine.
    const data = params && stringify ? JSON.stringify(params) : params;
    const axiosConfig: AxiosRequestConfig = {
      method,
      data,
      headers,
      baseURL,
      url: `${route}`,
      timeout: 600000,
      withCredentials: false,
      onUploadProgress,
    };
    axios.interceptors.response.use((response: AxiosResponse): any => {
      if (response && response.status === 500) {
        return response;
      }
      return response;
    });
    return axios(axiosConfig).catch((error: any) => {
      API.axiosError(error);
    });
  };

  static axiosError = (error: AxiosError): void => {
    if (
      error.config.baseURL === API_BASE_URL &&
      error.response &&
      error.response.status === 401
    ) {
      window.location.href = `${API_BASE_URL}/login?next=${window.location.pathname}`;
    } else if (
      error.config.baseURL === API_BASE_URL &&
      error.response &&
      error.response.status === 403
    ) {
      emitter.fire(NOT_AUTHORIZED_EVENT);
    } else if (_.get(error, "response.status") === 400) {
      toast.error(
        _.get(error, "response.data") && _.get(error, "response.data").message
          ? _.get(error, "response.data").message
          : _.get(error, "response.data")
      );
    } else {
      toast.error("Something Went Wrong");
    }
    throw _.get(error, "response.data") ? _.get(error, "response.data") : error;
  };

  static getAllHeaders = (headers: any) => {
    return {
      ...headers,
    };
  };
}

export default API;
